import { Form, FormQuestion, FormSubmission } from '../types'
import { slugify } from '../utils/format'
import { supabase } from './supabaseClient'
import { formConfig, formRow, questionRow, toForm, toQuestion } from './mappers'
import { getDb, refresh, saveConfig } from './store'
import { newId } from './authService'

export function listForms(organizationId: string): Form[] {
  return getDb().forms.filter((f) => f.organizationId === organizationId)
}

export function getForm(formId: string): Form | undefined {
  return getDb().forms.find((f) => f.id === formId)
}

export function getFormBySlug(slug: string): Form | undefined {
  return getDb().forms.find((f) => f.slug === slug)
}

export function listSubmissions(formId: string): FormSubmission[] {
  return getDb().submissions.filter((s) => s.formId === formId)
}

export function countSubmissions(organizationId: string, formId: string): number {
  return getDb().submissions.filter(
    (s) => s.organizationId === organizationId && s.formId === formId,
  ).length
}

export function emptyQuestion(formId: string, order: number): FormQuestion {
  return {
    id: newId('q'),
    formId,
    label: 'Nueva pregunta',
    type: 'texto_corto',
    options: [],
    required: false,
    order,
    isActive: true,
    conditionalLogic: {
      enabled: false,
      questionId: null,
      operator: 'igual',
      value: '',
    },
  }
}

/** Descarga un formulario público activo (visitante anónimo). */
export async function fetchPublicForm(slug: string): Promise<Form | null> {
  const { data } = await supabase
    .from('forms')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .is('deleted_at', null)
    .maybeSingle()
  if (!data) return null

  const [{ data: questions }, { data: settings }] = await Promise.all([
    supabase.from('form_questions').select('*').eq('form_id', data.id).order('position'),
    supabase
      .from('organization_settings')
      .select('value')
      .eq('organization_id', data.organization_id)
      .eq('key', `form_config:${data.id}`)
      .maybeSingle(),
  ])

  return toForm(data, settings?.value ?? {}, (questions ?? []).map(toQuestion))
}

/** Descarga por id el formulario asociado a una landing pública. */
export async function fetchPublicFormById(formId: string): Promise<Form | null> {
  const { data } = await supabase
    .from('forms')
    .select('*')
    .eq('id', formId)
    .is('deleted_at', null)
    .maybeSingle()
  if (!data) return null

  const [{ data: questions }, { data: settings }] = await Promise.all([
    supabase
      .from('form_questions')
      .select('*')
      .eq('form_id', data.id)
      .order('position'),
    supabase
      .from('organization_settings')
      .select('value')
      .eq('organization_id', data.organization_id)
      .eq('key', `form_config:${data.id}`)
      .maybeSingle(),
  ])

  return toForm(data, settings?.value ?? {}, (questions ?? []).map(toQuestion))
}

export async function createForm(
  organizationId: string,
  name: string,
): Promise<Form> {
  const form: Form = {
    id: newId('form'),
    organizationId,
    name,
    description: '',
    slug: slugify(name) || `formulario-${Date.now()}`,
    status: 'borrador',
    submitLabel: 'ENVIAR',
    successMessage: 'Hemos recibido tu solicitud.',
    contactFields: [
      { key: 'firstName', label: 'Nombre', type: 'texto_corto', required: true },
      { key: 'lastName', label: 'Apellidos', type: 'texto_corto', required: false },
      { key: 'company', label: 'Empresa', type: 'texto_corto', required: false },
      { key: 'email', label: 'Email', type: 'email', required: true },
      { key: 'phone', label: 'Teléfono', type: 'telefono', required: true },
    ],
    consentText:
      'Acepto la política de privacidad y autorizo que contacten conmigo.',
    questions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  const { error } = await supabase.from('forms').insert(formRow(form))
  if (error) throw new Error(error.message)
  await saveConfig(organizationId, 'form', form.id, formConfig(form))
  await refresh()
  return form
}

/**
 * Guarda el formulario completo: cabecera, configuración y preguntas.
 *
 * Cada operación indica en el mensaje de error qué paso ha fallado, para que
 * el editor pueda mostrar la causa real y permitir reintentar.
 */
export async function saveForm(form: Form): Promise<void> {
  // La fila del formulario puede no existir todavía (formularios creados
  // antes de conectar la base de datos): se inserta o actualiza.
  const { error } = await supabase
    .from('forms')
    .upsert(formRow(form), { onConflict: 'id' })
  if (error) {
    throw new Error(`No se ha podido guardar el formulario: ${error.message}`)
  }

  try {
    await saveConfig(form.organizationId, 'form', form.id, formConfig(form))
  } catch (err) {
    throw new Error(
      `No se ha podido guardar la configuración del formulario: ${
        err instanceof Error ? err.message : 'error desconocido'
      }`,
    )
  }

  // Las preguntas se reemplazan por completo para respetar orden y borrados.
  const { data: existing, error: readError } = await supabase
    .from('form_questions')
    .select('id')
    .eq('form_id', form.id)
  if (readError) {
    throw new Error(`No se han podido leer las preguntas: ${readError.message}`)
  }

  const keep = form.questions.map((q) => q.id)
  const remove = (existing ?? [])
    .map((row: { id: string }) => row.id)
    .filter((id: string) => !keep.includes(id))
  if (remove.length) {
    const { error: deleteError } = await supabase
      .from('form_questions')
      .delete()
      .in('id', remove)
    if (deleteError) {
      throw new Error(
        `No se han podido eliminar las preguntas borradas: ${deleteError.message}`,
      )
    }
  }

  if (form.questions.length) {
    const { error: questionsError } = await supabase
      .from('form_questions')
      .upsert(
        form.questions.map((question, index) =>
          questionRow(
            { ...question, order: index + 1 },
            form.id,
            form.organizationId,
          ),
        ),
        { onConflict: 'id' },
      )
    if (questionsError) {
      throw new Error(
        `No se han podido guardar las preguntas: ${questionsError.message}`,
      )
    }
  }
  await refresh()
}

export async function duplicateForm(formId: string): Promise<Form | undefined> {
  const original = getForm(formId)
  if (!original) return undefined
  const copy: Form = {
    ...(JSON.parse(JSON.stringify(original)) as Form),
    id: newId('form'),
    name: `${original.name} (copia)`,
    slug: `${original.slug}-copia-${Date.now().toString(36)}`,
    status: 'borrador',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  copy.questions = original.questions.map((question) => ({
    ...question,
    id: newId('q'),
    formId: copy.id,
  }))
  const { error } = await supabase.from('forms').insert(formRow(copy))
  if (error) throw new Error(error.message)
  await saveForm(copy)
  return copy
}

export async function setFormStatus(
  formId: string,
  status: Form['status'],
): Promise<void> {
  const form = getForm(formId)
  if (!form) return
  const { error } = await supabase
    .from('forms')
    .update({ is_active: status === 'activo' })
    .eq('id', formId)
  if (error) throw new Error(error.message)
  await saveConfig(form.organizationId, 'form', formId, {
    ...formConfig(form),
    status,
  })
  await refresh()
}

export async function restoreForm(formId: string): Promise<void> {
  const { error } = await supabase
    .from('forms')
    .update({ deleted_at: null })
    .eq('id', formId)
  if (error) throw new Error(error.message)
  await refresh()
}

/** Borrado lógico: el formulario pasa a la papelera de formularios. */
export async function deleteForm(formId: string): Promise<void> {
  const { error } = await supabase
    .from('forms')
    .update({ deleted_at: new Date().toISOString(), is_active: false })
    .eq('id', formId)
  if (error) throw new Error(error.message)
  await supabase.from('landing_pages').update({ form_id: null }).eq('form_id', formId)
  await refresh()
}
